/**
 * What the settings tab's four e-mail boxes say when a deployment has not stored a message.
 *
 * WHY THIS EXISTS
 * ---------------
 * The boxes were blank on a fresh installation, and a blank box says nothing about what the product
 * will actually send. It sends the approved message — the one `domain/email/claim-message.ts`
 * builds, with its emphasis and its single red link — and an operator could neither see that nor
 * tell what typing here would cost them.
 *
 * So the boxes now carry the default as a HINT rather than as a value. Nothing is written to the
 * server, nothing is saved behind the operator, and an untouched screen still stores no template:
 * what changes is that the screen states what the default is and what replacing it does.
 *
 * WHAT A STORED TEMPLATE CANNOT DO — said here because the screen must not imply otherwise
 * ----------------------------------------------------------------------------------------
 * A stored template is plain text with four allowed placeholders (`domain/email/template.ts`, a
 * closed set by design). It therefore cannot reproduce two things the approved default has:
 *
 *   - the subject's Turkish possessive («… Sertifikanız»), which is computed from the record's own
 *     title by `domain/document-noun.ts` and has no placeholder — which is also why the subject box
 *     shows a worded hint rather than a `{{…}}` line that would produce «Sertifikası Sertifikanız»;
 *   - the bold runs and the red underlined link, which belong to the approved HTML rendering.
 *
 * Both limits are stated to the operator instead of being papered over.
 *
 * The sentences are held to the real message by `tests/fixtures/claim-email-default.json`: the
 * backend proves the message it builds still contains them, and this package proves the hint names
 * them. A change to the approved wording that forgets this screen fails on both sides.
 */

/** The subject box's hint: what is sent when the box is left alone. */
export const CLAIM_EMAIL_SUBJECT_HINT =
  'Boş bırakılırsa: kaydın kendi adı, iyelik hâliyle — «CXO 100 Seçici Kurul Üyeliği Sertifikanız»';

/**
 * The body box's hint: the approved message, as the recipient reads it.
 *
 * The record's own values are shown as the placeholders a stored template may use, so an operator
 * who does decide to write their own starts from names that will actually be substituted.
 */
export const CLAIM_EMAIL_BODY_HINT = [
  'Boş bırakılırsa aşağıdaki onaylı metin gönderilir:',
  '',
  'Merhaba,',
  '',
  'Happy Place to Work tarafından {{credential_title}} adınıza tanımlandı.',
  '',
  'Sertifikayı kabul etmek için aşağıdaki linki tıklayınız.',
  '',
  '(onay bağlantısı — kırmızı ve altı çizili)',
  '',
  'Bağlantı tek kullanımlıktır ve gönderim tarihinden itibaren 30 gün boyunca geçerlidir.',
  '',
  'Doğrulama kodunuz: {{credential_code}}',
  '',
  'Bu e-posta size yanlışlıkla ulaştıysa herhangi bir işlem yapmadan yok sayabilirsiniz.',
  '',
  'Saygılarımızla,',
  'Happy Place to Work',
].join('\n');

/** What saving a message of your own costs, and what it can never remove. */
export const CLAIM_EMAIL_STORED_RULE =
  'Buraya bir metin yazıp kaydederseniz onaylı metnin yerine o metin gönderilir ve düz metin olarak ' +
  'gider — kalın vurgular ve kırmızı bağlantı biçimi yalnız onaylı varsayılana aittir. Onay ' +
  'bağlantısı her hâlükârda eklenir; yer tutucu olarak yalnız {{holder_name}}, {{credential_title}}, ' +
  '{{credential_url}} ve {{credential_code}} kullanılabilir.';
